import { Overlay } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  TemplateRef,
  ViewContainerRef,
  computed,
  inject,
  viewChild,
} from '@angular/core';
import { SHELL_POPOVER_ABOVE, ShellPopover } from '../../cabinet/layout/shell-popover';
import { ThemeService } from '../theme.service';
import type { Theme } from '../theme.service';

export interface ThemeChoice {
  value: Theme;
  label: string;
}

/** `system` follows the device setting; the UI calls it Auto. */
export const THEME_CHOICES: readonly ThemeChoice[] = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'Auto' },
];

const ARROW_STEPS: Readonly<Record<string, number>> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

let nextId = 0;

/**
 * A button showing the current theme that opens three window previews
 * (Light, Dark, Auto) as a radio group. Picking one applies it at once.
 */
@Component({
  selector: 'app-theme-picker',
  templateUrl: './theme-picker.component.html',
  styleUrl: './theme-picker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ThemePickerComponent {
  private readonly themeService = inject(ThemeService);

  private readonly trigger = viewChild.required<ElementRef<HTMLButtonElement>>('trigger');
  private readonly panelTemplate = viewChild.required<TemplateRef<unknown>>('panel');

  readonly popupId = `theme-picker-${nextId++}`;
  readonly choices = THEME_CHOICES;
  readonly current = computed<ThemeChoice>(
    () => THEME_CHOICES.find((c) => c.value === this.themeService.currentTheme()) ?? THEME_CHOICES[2],
  );

  readonly popover = new ShellPopover({
    overlay: inject(Overlay),
    viewContainerRef: inject(ViewContainerRef),
    destroyRef: inject(DestroyRef),
    trigger: () => this.trigger(),
    template: () => this.panelTemplate(),
    positions: SHELL_POPOVER_ABOVE,
  });

  toggle(): void {
    this.popover.toggle((panel) => {
      this.linkPopup(panel);
      panel.querySelector<HTMLElement>('[role="radio"][aria-checked="true"]')?.focus();
    });
  }

  choose(theme: Theme): void {
    this.apply(theme);
    this.popover.close(true);
  }

  onGroupKeydown(event: KeyboardEvent): void {
    if (event.key === 'Tab') {
      event.preventDefault();
      this.popover.close(true);
      return;
    }
    const step = ARROW_STEPS[event.key];
    if (!step) return;
    event.preventDefault();
    const index = THEME_CHOICES.findIndex((c) => c.value === this.current().value);
    const next = THEME_CHOICES[(index + step + THEME_CHOICES.length) % THEME_CHOICES.length].value;
    this.apply(next);
    this.popover.panel?.querySelector<HTMLElement>(`[data-theme-value="${next}"]`)?.focus();
  }

  private apply(theme: Theme): void {
    if (theme !== this.themeService.currentTheme()) this.themeService.setTheme(theme);
  }

  /** The trigger may sit in a shadow root, where an id can't reach the overlay; an element reference can. */
  private linkPopup(panel: HTMLElement): void {
    const trigger = this.trigger().nativeElement;
    const group = panel.querySelector('[role="radiogroup"]');
    if (group && 'ariaControlsElements' in trigger) trigger.ariaControlsElements = [group];
  }
}
